import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, App, Card, Col, DatePicker, Empty, Grid, Row, Segmented, Select, Space, Spin, Statistic, Table, Tooltip,
  Typography, theme,
} from 'antd';
import { CheckCircleFilled, ClockCircleFilled, EditFilled, InfoCircleOutlined } from '@ant-design/icons';
import {
  Bar, BarChart, CartesianGrid, Cell, LabelList, Pie, PieChart, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis,
} from 'recharts';
import dayjs from 'dayjs';
import { AYLAR, hata, kisaPara, para, useHarcirahApi } from './ortak';

/**
 * Harcırah raporu (23.10.2026) — routes/harcirah.py GET /harcirah/rapor. Tutarlar çalışanın girdiği
 * öğün tutarlarıdır; seçilen aralığa düşen günler sayılır. Renkler dataviz referans paletinin 1–2. kategorik
 * slotları (öğle mavi, akşam turuncu; açık/koyu yüzeyde doğrulandı), durum renkleri ayrılmış durum paleti.
 * Tür payı halkası ve tür × şehir ısı haritası tek tonlu (mavi) sıralı rampa kullanır: 9 kategoriye 9 renk CVD'de
 * ayırt edilemez; kimliği etiket taşır, renk büyüklüğü. Halka ≤ 6 dilim (ilk 5 + Diğer).
 * 24.10.2026 seyahat giderleri (şirket öder): maliyet = yemek harcırahı + ulaşım + konaklama; giderler seyahatin
 * dönemine yazılır (backend rapor()).
 * 25.10.2026 kategori ayrımı (kullanıcı: "seyahat, konaklama ve gıda olarak ayrım"): üstteki Kategori seçimi
 * (Tümü / Gıda / Seyahat / Konaklama) her grafiği o kategoriye çevirir; Tümü'nde çubuklar üç kategoriye yığılır.
 * Renk kategoriyi taşır, her yerde aynı: gıda mavi (slot 1), seyahat turuncu (slot 2), konaklama yeşil (slot 3) —
 * validate_palette açık/koyu PASS (açıkta yeşil yüzeye 3:1 altı → lejant + tablo + ipucu). Gıdanın içindeki öğle /
 * akşam aynı mavinin açık / koyu tonu (--ordinal PASS). "Seyahat" = ulaşım (bilet, kiralama, yakıt); seyahat SAYISI
 * ve seyahat TÜRÜ ile karışmasın diye etiketlerde "Seyahat (ulaşım)".
 * 30.10.2026 "En çok harcama yapanlar": özet kartlarının altında, seçili kategoride ilk 10 kişi — sıra, yatay çubuk
 * (Tümü'nde gıda / seyahat / konaklama yığını, 2px boşluk, uçta 4px yuvarlak), tutar, toplam içindeki pay ve gün
 * başı tutar; satıra gelince kırılım. Kişi süzgeci seçiliyken gösterilmez (tek kişi).
 * 30.10.2026 rapor kapsamı (backend zorlar): yönetim + Harcırah r tamamını görür ve Ekip süzgeci kullanır (Satış ve
 * Operasyon Koordinatörü / İhale ve Kiralama Müdürü altındakiler sabit ekip, kalanlar amirine göre); amir yalnız amiri
 * olduğu seyahatleri görür (veri.kapsam === 'ekip').
 */
const { Text } = Typography;
const { RangePicker } = DatePicker;

const DURUM_SECENEK = [
  { value: 'tumu', label: 'Tümü' },
  { value: 'taslak', label: 'Taslak' },
  { value: 'bekleyen', label: 'Onay Bekleyen' },
  { value: 'onaylandi', label: 'Onaylanan' },
];
const DURUM_GRUBU = {
  onaylandi: { ad: 'Onaylanan', renk: '#0ca30c', ikon: <CheckCircleFilled /> },
  bekleyen: { ad: 'Onay Bekleyen', renk: '#fab219', ikon: <ClockCircleFilled /> },
  taslak: { ad: 'Taslak / Geri Gönderilen', renk: null, ikon: <EditFilled /> },
};
// Eksen: düz binlik ayraçlı sayı ("1,6 B" kısaltması milyar sanılabiliyor)
const eksen = (v) => Number(v).toLocaleString('tr-TR', { maximumFractionDigits: 0 });

function useRenkler() {
  const { token } = theme.useToken();
  return useMemo(() => {
    const h = token.colorBgContainer.replace('#', '');
    const koyu = h.length >= 6 && (parseInt(h.slice(0, 2), 16) + parseInt(h.slice(2, 4), 16) + parseInt(h.slice(4, 6), 16)) / 3 < 128;
    return {
      gida: koyu ? '#3987e5' : '#2a78d6',
      ogle: koyu ? '#9ec5f4' : '#86b6ef',
      aksam: koyu ? '#2a78d6' : '#1c5cab',
      ulasim: koyu ? '#d95926' : '#eb6834',
      konaklama: koyu ? '#199e70' : '#1baf7a',
      yuzey: token.colorBgContainer,
      cizgi: token.colorBorderSecondary,
      yazi: token.colorText,
      yazi2: token.colorTextSecondary,
      notr: koyu ? '#5c5b57' : '#c3c2b7',
      koyu,
      // Halka: büyükten küçüğe (en büyük dilim en belirgin) — sıralı rampa, açık/koyu ayrı doğrulandı (--ordinal).
      // Isı haritası: küçükten büyüğe; düşük değer yüzeye yaklaşır. Seçili kategorinin tonunda: gıda / tümü mavi
      // (palet rampası), seyahat turuncu, konaklama yeşil (OKLCH'de ton sabit üretildi, --ordinal PASS).
      halkalar: {
        mavi: koyu ? ['#9ec5f4', '#6da7ec', '#3987e5', '#256abf', '#184f95'] : ['#104281', '#1c5cab', '#2a78d6', '#5598e7', '#86b6ef'],
        turuncu: koyu ? ['#fec1ab', '#fd946e', '#ee6b37', '#cf4e12', '#a83a00'] : ['#7b2903', '#a33903', '#cb4b0b', '#ee6b37', '#ff9973'],
        yesil: koyu ? ['#6deeb5', '#4cd099', '#22b37d', '#039465', '#097650'] : ['#004a30', '#026644', '#048359', '#08a26f', '#37bf89'],
      },
      isilar: {
        mavi: koyu
          ? ['#0d366b', '#104281', '#184f95', '#1c5cab', '#256abf', '#2a78d6', '#3987e5', '#5598e7', '#6da7ec', '#86b6ef', '#9ec5f4']
          : ['#cde2fb', '#b7d3f6', '#9ec5f4', '#86b6ef', '#6da7ec', '#5598e7', '#3987e5', '#2a78d6', '#256abf', '#1c5cab', '#184f95', '#104281', '#0d366b'],
        turuncu: koyu
          ? ['#472214', '#5b2b1a', '#6f351f', '#843e24', '#99482a', '#af522f', '#c65d34', '#dd673a', '#f57240', '#fe885d', '#fea382']
          : ['#ffe9e1', '#fed1c1', '#f6bba5', '#eca68c', '#e19073', '#d67b5a', '#ca6540', '#be4f21', '#ab3d06', '#923306', '#7b2903', '#632002'],
        yesil: koyu
          ? ['#133526', '#174431', '#1b543c', '#1f6347', '#237453', '#26845e', '#29956b', '#2da777', '#30b984', '#44c992', '#57daa2']
          : ['#dbf6e7', '#c1e7d3', '#a8d7bf', '#8ec8ab', '#74b997', '#58aa84', '#399b71', '#0f8b60', '#057952', '#066746', '#025639', '#02452d'],
      },
      kart: token.colorBgElevated,
      golge: token.boxShadowSecondary,
    };
  }, [token]);
}

const BARLAR = [{ key: 'ogle', ad: 'Öğle' }, { key: 'aksam', ad: 'Akşam' }];
const GIDER_KATEGORI = [{ key: 'ulasim', ad: 'Seyahat (Ulaşım)' }, { key: 'konaklama', ad: 'Konaklama' }];
// Kategori: grafiklerin serileri ve satırın değeri (backend her kırılımda ogle/aksam/ulasim/konaklama/toplam/maliyet verir;
// toplam = gıda)
const KATEGORI = {
  tumu: { ad: 'Toplam Maliyet', kisa: 'Maliyet', ton: 'mavi', deger: (x) => x.maliyet,
    seriler: [{ key: 'gida', ad: 'Gıda' }, { key: 'ulasim', ad: 'Seyahat (Ulaşım)' }, { key: 'konaklama', ad: 'Konaklama' }] },
  gida: { ad: 'Gıda', kisa: 'Gıda', ton: 'mavi', deger: (x) => x.toplam, seriler: BARLAR },
  seyahat: { ad: 'Seyahat (Ulaşım)', kisa: 'Seyahat (Ulaşım)', ton: 'turuncu', deger: (x) => x.ulasim,
    seriler: [{ key: 'ulasim', ad: 'Seyahat (Ulaşım)' }] },
  konaklama: { ad: 'Konaklama', kisa: 'Konaklama', ton: 'yesil', deger: (x) => x.konaklama,
    seriler: [{ key: 'konaklama', ad: 'Konaklama' }] },
};
const yuzde = (v, t) => `%${t ? Math.round((v / t) * 100) : 0}`;
// Dolgunun üstündeki yazı: açık dolguya koyu, koyu dolguya beyaz (kontrast)
const yaziRengi = (hex) => {
  const h = hex.replace('#', '');
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) / 255)
    .map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.35 ? '#0b0b0b' : '#ffffff';
};

// --- Tür payı halkası: ilk 5 tür + Diğer, büyükten küçüğe saat yönünde ------------------------
function TurHalkasi({ turler, r }) {
  const sirali = turler.filter((t) => t.toplam > 0).sort((a, b) => b.toplam - a.toplam);
  const genel = sirali.reduce((a, t) => a + t.toplam, 0);
  const kalan = sirali.slice(5);
  const veri = [
    ...sirali.slice(0, 5).map((t, i) => ({ ...t, renk: r.halka[i] })),
    ...(kalan.length ? [{
      tur: 'diger', ad: `Diğer (${kalan.length} Tür)`, renk: r.notr, alt: kalan,
      toplam: kalan.reduce((a, t) => a + t.toplam, 0), seyahat: kalan.reduce((a, t) => a + t.seyahat, 0),
      gun: kalan.reduce((a, t) => a + t.gun, 0),
    }] : []),
  ];
  const dilimEtiketi = ({ cx, cy, midAngle, innerRadius, outerRadius, index }) => {
    const d = veri[index];
    if (d.toplam / genel < 0.07) return null;  // dar dilime yazı sığmaz; lejantta var
    const rad = (innerRadius + outerRadius) / 2;
    const x = cx + rad * Math.cos((-midAngle * Math.PI) / 180);
    const y = cy + rad * Math.sin((-midAngle * Math.PI) / 180);
    return <text x={x} y={y} fill={yaziRengi(d.renk)} textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={600}>{yuzde(d.toplam, genel)}</text>;
  };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16 }}>
      <div style={{ position: 'relative', width: 230, height: 230, flex: '0 0 230px', margin: '0 auto' }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={veri} dataKey="toplam" nameKey="ad" innerRadius="56%" outerRadius="96%" startAngle={90} endAngle={-270}
              stroke={r.yuzey} strokeWidth={2} isAnimationActive={false} labelLine={false} label={dilimEtiketi}>
              {veri.map((d) => <Cell key={d.tur} fill={d.renk} />)}
            </Pie>
            <RTooltip content={(p) => <IpucuKutusu {...p} r={r} baslik={(d) => d.ad}
              satirlar={(d) => [['Tutar', para(d.toplam)], ['Pay', yuzde(d.toplam, genel)], ['Seyahat / Gün', `${d.seyahat} / ${d.gun}`],
                ...(d.alt || []).map((a) => [`· ${a.ad}`, para(a.toplam)])]} />} />
          </PieChart>
        </ResponsiveContainer>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Toplam</Text>
          <Text strong style={{ fontSize: 17, fontVariantNumeric: 'tabular-nums' }}>{kisaPara(genel)} ₺</Text>
        </div>
      </div>
      <div style={{ flex: '1 1 220px', minWidth: 0 }}>
        {veri.map((d) => (
          <div key={d.tur} style={{ display: 'grid', gridTemplateColumns: '12px minmax(0, 1fr) auto auto', gap: 8, alignItems: 'center',
            padding: '5px 0', borderBottom: `1px solid ${r.cizgi}` }}>
            <span style={{ width: 12, height: 12, borderRadius: 3, background: d.renk }} />
            <Tooltip title={d.alt ? d.alt.map((a) => <div key={a.tur}>{a.ad}: {para(a.toplam)}</div>) : null}>
              <Text ellipsis style={{ color: r.yazi }}>{d.ad}</Text>
            </Tooltip>
            <Text strong style={{ fontVariantNumeric: 'tabular-nums' }}>{yuzde(d.toplam, genel)}</Text>
            <Text type="secondary" style={{ fontVariantNumeric: 'tabular-nums', minWidth: 90, textAlign: 'right' }}>{para(d.toplam)}</Text>
          </div>
        ))}
      </div>
    </div>
  );
}

// --- Tür × şehir ısı haritası: satır tür, sütun şehir (en çok 12; kalanı "Diğer iller") ----------
function TurIlIsiHaritasi({ turIl, turler, r }) {
  const ilToplam = {};
  turIl.forEach((x) => { ilToplam[x.il] = (ilToplam[x.il] || 0) + x.toplam; });
  const tumIller = Object.keys(ilToplam).filter((il) => ilToplam[il] > 0).sort((a, b) => ilToplam[b] - ilToplam[a]);
  const iller = tumIller.length > 12 ? [...tumIller.slice(0, 11), 'Diğer İller'] : tumIller;
  const ilAdi = (il) => (iller.includes(il) ? il : 'Diğer İller');
  const hucre = {};
  turIl.forEach((x) => {
    const k = `${x.tur}|${ilAdi(x.il)}`;
    hucre[k] = hucre[k] || { toplam: 0, gun: 0 };
    hucre[k].toplam += x.toplam;
    hucre[k].gun += x.gun;
  });
  const satirlar = turler.filter((t) => t.toplam > 0).sort((a, b) => b.toplam - a.toplam);
  const enCok = Math.max(1, ...Object.values(hucre).map((h) => h.toplam));
  const renk = (v) => r.isi[Math.min(r.isi.length - 1, Math.max(0, Math.ceil((v / enCok) * r.isi.length) - 1))];
  const sutunToplam = (il) => satirlar.reduce((a, t) => a + (hucre[`${t.tur}|${il}`]?.toplam || 0), 0);
  const izgara = { display: 'grid', gridTemplateColumns: `130px repeat(${iller.length}, minmax(58px, 1fr)) 84px`, gap: 2, minWidth: 130 + iller.length * 60 + 86 };
  const baslik = { fontSize: 11, color: r.yazi2, padding: '4px 2px', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <div style={izgara} role="table" aria-label="Seyahat türü ve şehre göre harcırah">
          <div style={baslik} />
          {iller.map((il) => <div key={il} style={baslik} title={il}>{il}</div>)}
          <div style={{ ...baslik, textAlign: 'right' }}>Toplam</div>
          {satirlar.map((t) => (
            <React.Fragment key={t.tur}>
              <div style={{ fontSize: 12, padding: '0 6px', display: 'flex', alignItems: 'center', color: r.yazi, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }} title={t.ad}>{t.ad}</div>
              {iller.map((il) => {
                const h = hucre[`${t.tur}|${il}`];
                if (!h || !h.toplam) return <div key={il} style={{ height: 34, borderRadius: 4, background: r.cizgi, opacity: 0.35 }} />;
                const dolgu = renk(h.toplam);
                return (
                  <Tooltip key={il} title={`${t.ad} · ${il}: ${para(h.toplam)} · ${h.gun} Gün`}>
                    <div style={{ height: 34, borderRadius: 4, background: dolgu, color: yaziRengi(dolgu), fontSize: 11, fontWeight: 600,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontVariantNumeric: 'tabular-nums', cursor: 'default' }}>
                      {eksen(h.toplam)}
                    </div>
                  </Tooltip>
                );
              })}
              <div style={{ fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 4, color: r.yazi, fontVariantNumeric: 'tabular-nums' }}>{eksen(t.toplam)}</div>
            </React.Fragment>
          ))}
          <div style={{ ...baslik, textAlign: 'left', paddingLeft: 6, fontWeight: 600 }}>Toplam</div>
          {iller.map((il) => <div key={il} style={{ ...baslik, fontWeight: 600, color: r.yazi }}>{eksen(sutunToplam(il))}</div>)}
          <div />
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
        <Text type="secondary" style={{ fontSize: 11 }}>0</Text>
        <div style={{ flex: '0 1 220px', height: 8, borderRadius: 4, background: `linear-gradient(90deg, ${r.isi.join(', ')})` }} />
        <Text type="secondary" style={{ fontSize: 11 }}>{eksen(enCok)} ₺</Text>
        <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>Koyulaştıkça tutar artar; boş kutu = o türde o şehre seyahat yok.</Text>
      </div>
    </div>
  );
}

function Aciklama({ r, seriler = BARLAR }) {
  return (
    <Space size={16} style={{ marginBottom: 8 }}>
      {seriler.map((s) => (
        <Space key={s.key} size={6}>
          <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: r[s.key] }} />
          <Text type="secondary" style={{ fontSize: 12 }}>{s.ad}</Text>
        </Space>
      ))}
    </Space>
  );
}

function IpucuKutusu({ active, payload, label, r, baslik, satirlar }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div style={{ background: r.kart, boxShadow: r.golge, borderRadius: 6, padding: '8px 12px', fontSize: 12, minWidth: 160 }}>
      <div style={{ fontWeight: 600, marginBottom: 4, color: r.yazi }}>{baslik ? baslik(d, label) : label}</div>
      {satirlar(d).map(([ad, deger, renk]) => (
        <div key={ad} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, color: r.yazi }}>
          <span>{renk && <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: renk, marginRight: 6 }} />}{ad}</span>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{deger}</span>
        </div>
      ))}
    </div>
  );
}

// Başlık gövdede, satır kırabilir: antd kart başlığı dar ekranda (extra ile) sıfır genişliğe düşüyordu
function KartBasligi({ baslik, aciklama, sag }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
      <Space size={6}>
        <Text strong>{baslik}</Text>
        {aciklama && <Tooltip title={aciklama}><InfoCircleOutlined style={{ color: '#8c8c8c' }} /></Tooltip>}
      </Space>
      {sag}
    </div>
  );
}

function GrafikKarti({ baslik, aciklama, tablo, children }) {
  const [gorunum, setGorunum] = useState('grafik');
  return (
    <Card size="small" style={{ height: '100%' }}>
      <KartBasligi baslik={baslik} aciklama={aciklama} sag={(
        <Segmented size="small" value={gorunum} onChange={setGorunum}
          options={[{ value: 'grafik', label: 'Grafik' }, { value: 'tablo', label: 'Tablo' }]} />
      )} />
      {gorunum === 'grafik' ? children : tablo}
    </Card>
  );
}

// Yatay çubuk: satır = kişi / il; yığılmış öğle + akşam ya da tek seri
function YatayCubuk({ veri, ad, r, seriler, yigilmis, etiket, ipucu }) {
  const genis = Grid.useBreakpoint().md;
  const yukseklik = 40 + veri.length * 34;
  const son = seriler[seriler.length - 1].key;
  return (
    <>
      {seriler.length > 1 && <Aciklama r={r} seriler={seriler} />}
      <ResponsiveContainer width="100%" height={yukseklik}>
        <BarChart data={veri} layout="vertical" margin={{ top: 4, right: genis ? 72 : 52, bottom: 4, left: 4 }}
          barCategoryGap={yigilmis ? 8 : 6} barGap={2}>
          <CartesianGrid horizontal={false} stroke={r.cizgi} />
          <XAxis type="number" tickFormatter={eksen} tick={{ fill: r.yazi2, fontSize: 11 }}
            axisLine={false} tickLine={false} />
          <YAxis type="category" dataKey={ad} width={genis ? 150 : 96} tick={{ fill: r.yazi, fontSize: 12 }}
            axisLine={{ stroke: r.cizgi }} tickLine={false} />
          <RTooltip cursor={{ fill: r.cizgi, opacity: 0.4 }} content={(p) => <IpucuKutusu {...p} r={r} satirlar={ipucu} />} />
          {seriler.map((s) => (
            <Bar key={s.key} dataKey={s.key} stackId={yigilmis ? 'a' : undefined} fill={r[s.key]}
              stroke={r.yuzey} strokeWidth={yigilmis ? 2 : 0} maxBarSize={yigilmis ? 20 : 12}
              radius={!yigilmis || s.key === son ? [0, 4, 4, 0] : 0} isAnimationActive={false}>
              {etiket && (yigilmis ? s.key === son : true) && (
                <LabelList dataKey={etiket.alan || s.key} position="right" formatter={(v) => (v ? etiket.bicim(v) : '')}
                  style={{ fill: r.yazi2, fontSize: 11 }} />
              )}
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    </>
  );
}

function DurumCubugu({ durumlar, r }) {
  const toplam = durumlar.reduce((a, d) => a + d.toplam, 0);
  const sira = ['onaylandi', 'bekleyen', 'taslak'];
  const renk = (g) => DURUM_GRUBU[g].renk || r.notr;
  return (
    <div>
      <div style={{ display: 'flex', height: 14, borderRadius: 4, overflow: 'hidden', background: r.cizgi, gap: toplam ? 2 : 0 }}>
        {toplam > 0 && sira.map((g) => {
          const d = durumlar.find((x) => x.grup === g);
          return d && d.toplam > 0 ? (
            <Tooltip key={g} title={`${DURUM_GRUBU[g].ad}: ${para(d.toplam)} (${d.seyahat} Seyahat)`}>
              <div style={{ width: `${(d.toplam / toplam) * 100}%`, background: renk(g) }} />
            </Tooltip>
          ) : null;
        })}
      </div>
      <Space size={20} wrap style={{ marginTop: 10 }}>
        {sira.map((g) => {
          const d = durumlar.find((x) => x.grup === g) || { toplam: 0, seyahat: 0 };
          return (
            <Space key={g} size={6}>
              <span style={{ color: renk(g) }}>{DURUM_GRUBU[g].ikon}</span>
              <Text type="secondary" style={{ fontSize: 12 }}>{DURUM_GRUBU[g].ad}</Text>
              <Text strong>{para(d.toplam)}</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>· {d.seyahat} Seyahat</Text>
            </Space>
          );
        })}
      </Space>
    </div>
  );
}

// --- En çok harcama yapanlar: seçili kategoride ilk 10 kişi ---------------------------------------------------
const ILK = 10;
const EC_STIL = `
  .ec-satir, .ec-bas { display: grid; grid-template-columns: 28px minmax(140px, 220px) minmax(0, 1fr) 118px 48px 128px;
                       grid-template-areas: "sira kisi cubuk tutar pay gun"; column-gap: 12px; align-items: center; }
  .ec-satir { padding: 7px 4px; border-bottom: 1px solid var(--ec-cizgi); border-radius: 4px; outline: none; }
  .ec-satir:hover, .ec-satir:focus-visible { background: var(--ec-vurgu); }
  .ec-bas { font-size: 11px; color: var(--ec-yazi2); padding: 0 4px 4px; border-bottom: 1px solid var(--ec-cizgi); }
  .ec-sira { grid-area: sira; font-weight: 700; color: var(--ec-yazi2); text-align: right; font-variant-numeric: tabular-nums; }
  .ec-kisi { grid-area: kisi; min-width: 0; }
  .ec-ad { font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .ec-alt { font-size: 12px; color: var(--ec-yazi2); }
  .ec-cubuk { grid-area: cubuk; height: 12px; }
  .ec-dolgu { height: 100%; display: flex; gap: 2px; }
  .ec-dolgu > div { height: 100%; min-width: 2px; }
  .ec-dolgu > div:last-child { border-radius: 0 4px 4px 0; }
  .ec-tutar { grid-area: tutar; text-align: right; font-weight: 700; font-variant-numeric: tabular-nums; white-space: nowrap; }
  .ec-pay { grid-area: pay; text-align: right; color: var(--ec-yazi2); font-variant-numeric: tabular-nums; }
  .ec-gun { grid-area: gun; text-align: right; color: var(--ec-yazi2); font-variant-numeric: tabular-nums; white-space: nowrap; }
  .ec-mobil { display: none; }
  @media (max-width: 640px) {
    .ec-bas { display: none; }
    .ec-satir { grid-template-columns: 22px minmax(0, 1fr) auto; row-gap: 4px;
                grid-template-areas: "sira kisi tutar" "sira cubuk cubuk"; }
    .ec-pay, .ec-gun { display: none; }
    .ec-mobil { display: inline; }
  }
`;

function EnCokHarcayanlar({ kisiler, K, r, toplam, ipucu }) {
  const liste = kisiler.slice(0, ILK);
  const enCok = liste[0]?.deger || 1;
  const gunBasi = (d) => (d.gun ? `${para(d.deger / d.gun)} / Gün` : '—');
  return (
    <div style={{ '--ec-cizgi': r.cizgi, '--ec-yazi2': r.yazi2, '--ec-vurgu': r.koyu ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.025)' }}>
      <style>{EC_STIL}</style>
      {K.seriler.length > 1 && <Aciklama r={r} seriler={K.seriler} />}
      <div className="ec-bas" aria-hidden="true">
        <span /><span>Kişi</span><span /><span style={{ textAlign: 'right' }}>{K.ad}</span>
        <span style={{ textAlign: 'right' }}>Pay</span><span style={{ textAlign: 'right' }}>Seyahat Günü Başı</span>
      </div>
      {liste.map((d, i) => (
        <Tooltip key={d.kullanici_no} placement="topLeft" color={r.kart}
          title={(
            <div style={{ color: r.yazi, fontSize: 12, minWidth: 180 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>{i + 1}. {d.ad}</div>
              {[...ipucu(d), ['Seyahat / Gün', `${d.seyahat} / ${d.gun}`], ['Gün Başı', gunBasi(d)]].map(([ad, deger, renk]) => (
                <div key={ad} style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
                  <span>{renk && <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: renk, marginRight: 6 }} />}{ad}</span>
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>{deger}</span>
                </div>
              ))}
            </div>
          )}>
          <div className="ec-satir" tabIndex={0}
            aria-label={`${i + 1}. ${d.ad}: ${para(d.deger)}, toplamın ${yuzde(d.deger, toplam)}, ${d.seyahat} seyahat, ${d.gun} gün`}>
            <span className="ec-sira">{i + 1}</span>
            <div className="ec-kisi">
              <div className="ec-ad">{d.ad}</div>
              <div className="ec-alt">
                {d.seyahat} Seyahat · {d.gun} Gün
                <span className="ec-mobil"> · {yuzde(d.deger, toplam)} · {gunBasi(d)}</span>
              </div>
            </div>
            <div className="ec-cubuk">
              <div className="ec-dolgu" style={{ width: `${(d.deger / enCok) * 100}%` }}>
                {K.seriler.filter((sr) => d[sr.key] > 0).map((sr) => (
                  <div key={sr.key} style={{ flex: `${d[sr.key]} 0 0`, background: r[sr.key] }} />
                ))}
              </div>
            </div>
            <span className="ec-tutar">{para(d.deger)}</span>
            <span className="ec-pay">{yuzde(d.deger, toplam)}</span>
            <span className="ec-gun">{gunBasi(d)}</span>
          </div>
        </Tooltip>
      ))}
      {kisiler.length > ILK && (
        <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 8 }}>
          Diğer {kisiler.length - ILK} kişi “Kişi Bazında” grafiğinde.
        </Text>
      )}
    </div>
  );
}

const ARALIKLAR = [
  { label: 'Bu Ay', value: [dayjs().startOf('month'), dayjs().endOf('month')] },
  { label: 'Geçen Ay', value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
  { label: 'Son 3 Ay', value: [dayjs().subtract(2, 'month').startOf('month'), dayjs().endOf('month')] },
  { label: 'Bu Yıl', value: [dayjs().startOf('year'), dayjs().endOf('year')] },
];

// Özet kartı: tıklanınca o kategoriye geçer; seçili kategori vurgulu
function KategoriKarti({ secili, onSec, baslik, deger, renk, children }) {
  return (
    <Card size="small" hoverable onClick={onSec}
      style={{ height: '100%', borderColor: secili ? renk : undefined, boxShadow: secili ? `inset 0 0 0 1px ${renk}` : undefined }}>
      <Statistic title={<span>{renk && <span style={{ color: renk }}>■ </span>}{baslik}</span>} value={deger} formatter={(v) => para(v)} />
      {children}
    </Card>
  );
}

export default function HarcirahRapor() {
  const api = useHarcirahApi();
  const { message } = App.useApp();
  const r = useRenkler();
  const [aralik, setAralik] = useState(ARALIKLAR[0].value);
  const [durum, setDurum] = useState('tumu');
  const [kategori, setKategori] = useState('tumu');
  const [kisi, setKisi] = useState();
  const [ekip, setEkip] = useState();
  const [kisiler, setKisiler] = useState([]);
  const [turler, setTurler] = useState([]);
  const [turSecenekleri, setTurSecenekleri] = useState([]);
  const [veri, setVeri] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);

  useEffect(() => { api.get('/personel').then(setKisiler).catch(() => {}); }, [api]);
  useEffect(() => {
    api.get('/secenekler').then((d) => setTurSecenekleri([...d.seyahat_turleri, { value: 'belirtilmemis', label: 'Belirtilmemiş' }]))
      .catch(() => {});
  }, [api]);
  useEffect(() => {
    setYukleniyor(true);
    api.get('/rapor', {
      bas: aralik[0].format('YYYY-MM-DD'), bit: aralik[1].format('YYYY-MM-DD'), durum, kisi,
      seyahat_turu: turler.length ? turler.join(',') : undefined, ekip,
    }).then(setVeri).catch((e) => message.error(hata(e))).finally(() => setYukleniyor(false));
  }, [api, aralik, durum, kisi, turler, ekip, message]);
  const ekipSecenekleri = [['Sabit Ekipler', true], ['Amire Göre', false]].map(([ad, sabit]) => ({
    label: ad,
    options: (veri?.ekipler || []).filter((e) => e.sabit === sabit).map((e) => ({ value: e.ekip, label: `${e.ad} (${e.seyahat})` })),
  })).filter((g) => g.options.length);

  const K = KATEGORI[kategori];
  const rTon = { ...r, halka: r.halkalar[K.ton], isi: r.isilar[K.ton] };  // halka + ısı haritası kategorinin tonunda
  // Satır: seçili kategorinin değeri (`deger`, etiket ve sıralama) + yığın için gıda (= öğle + akşam)
  const satir = (x) => ({ ...x, gida: x.toplam, deger: K.deger(x) });
  const dolu = (l) => l.map(satir).filter((x) => x.deger > 0).sort((a, b) => b.deger - a.deger);
  const zaman = useMemo(() => (veri?.zaman || []).map((z) => ({
    ...z, gida: z.toplam, deger: KATEGORI[kategori].deger(z),
    etiket: veri.periyot === 'ay' ? `${AYLAR[dayjs(z.donem).month()]} ${dayjs(z.donem).format('YY')}` : dayjs(z.donem).format('DD.MM'),
  })), [veri, kategori]);
  const o = veri?.ozet;
  const ipucu = (d) => (kategori === 'tumu'
    ? [['Gıda', para(d.gida), r.gida], ['· Öğle / Akşam', `${para(d.ogle)} / ${para(d.aksam)}`],
      ['Seyahat (Ulaşım)', para(d.ulasim), r.ulasim], ['Konaklama', para(d.konaklama), r.konaklama], ['Toplam Maliyet', para(d.maliyet)]]
    : kategori === 'gida'
      ? [['Öğle', para(d.ogle), r.ogle], ['Akşam', para(d.aksam), r.aksam], ['Gıda Toplamı', para(d.toplam)]]
      : [[K.ad, para(d.deger), r[K.seriler[0].key]]]);
  const katSutunlari = [
    { title: 'Gıda', dataIndex: 'toplam', align: 'right', render: para },
    { title: 'Seyahat (Ulaşım)', dataIndex: 'ulasim', align: 'right', render: para },
    { title: 'Konaklama', dataIndex: 'konaklama', align: 'right', render: para },
    { title: 'Toplam Maliyet', dataIndex: 'maliyet', align: 'right', render: (v) => <b>{para(v)}</b> },
  ];
  const baslik = (ad) => `${ad} — ${K.kisa}`;
  const gidaGoster = kategori === 'tumu' || kategori === 'gida';
  const giderKartlari = o && o.gider > 0 ? [
    (kategori === 'tumu' || kategori === 'seyahat') && 'turler',
    (kategori === 'tumu' || kategori === 'seyahat') && veri.ulasim_sekilleri.length > 0 && 'ulasim',
    (kategori === 'tumu' || kategori === 'konaklama') && 'konaklama',
  ].filter(Boolean) : [];
  const giderGenislik = { xs: 24, lg: giderKartlari.length === 1 ? 24 : 12, xl: 24 / (giderKartlari.length || 1) };

  return (
    <div>
      <Space wrap style={{ marginBottom: 12 }}>
        <RangePicker value={aralik} onChange={(v) => v && setAralik(v)} presets={ARALIKLAR} format="DD.MM.YYYY" allowClear={false} />
        <Segmented value={durum} onChange={setDurum} options={DURUM_SECENEK} />
        <Select allowClear placeholder="Tüm Kişiler" style={{ width: 220 }} value={kisi} onChange={setKisi}
          showSearch optionFilterProp="label"
          options={kisiler.map((k) => ({ value: k.kullanici_no, label: k.personel_adi }))} />
        <Select mode="multiple" allowClear placeholder="Tüm Seyahat Türleri" style={{ minWidth: 240, maxWidth: 480 }}
          value={turler} onChange={setTurler} options={turSecenekleri} maxTagCount="responsive" optionFilterProp="label" />
        {veri?.kapsam === 'tam' && (
          <Select allowClear showSearch placeholder="Tüm Ekipler" style={{ minWidth: 260 }} value={ekip} onChange={setEkip}
            options={ekipSecenekleri} optionFilterProp="label" aria-label="Ekip" />
        )}
      </Space>
      {veri?.kapsam === 'ekip' && (
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message="Yalnız amiri olduğunuz seyahatler (ekibiniz) görünür." />
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <Text strong>Kategori:</Text>
        <Segmented value={kategori} onChange={setKategori} options={[
          { value: 'tumu', label: 'Tümü' },
          { value: 'gida', label: <span><span style={{ color: r.gida }}>■</span> Gıda</span> },
          { value: 'seyahat', label: <span><span style={{ color: r.ulasim }}>■</span> Seyahat (Ulaşım)</span> },
          { value: 'konaklama', label: <span><span style={{ color: r.konaklama }}>■</span> Konaklama</span> },
        ]} />
        <Text type="secondary" style={{ fontSize: 12 }}>
          Gıda = yemek harcırahı (çalışana ödenir) · Seyahat = uçak bileti, araç kiralama, yakıt · Konaklama = otel (şirket öder)
        </Text>
      </div>
      <Spin spinning={yukleniyor}>
        {o && (
          <>
            <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
              <Col xs={24} sm={12} lg={6}>
                <KategoriKarti secili={kategori === 'tumu'} onSec={() => setKategori('tumu')} baslik="Toplam Maliyet" deger={o.maliyet}>
                  <Text type="secondary" style={{ fontSize: 12 }}>Gıda + Seyahat + Konaklama</Text>
                </KategoriKarti>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <KategoriKarti secili={kategori === 'gida'} onSec={() => setKategori('gida')} baslik="Gıda (Çalışana)" deger={o.toplam} renk={r.gida}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 12px', marginTop: 4 }}>
                    <Text style={{ fontSize: 12 }}><span style={{ color: r.ogle }}>■</span> Öğle {para(o.ogle)}</Text>
                    <Text style={{ fontSize: 12 }}><span style={{ color: r.aksam }}>■</span> Akşam {para(o.aksam)}</Text>
                  </div>
                </KategoriKarti>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <KategoriKarti secili={kategori === 'seyahat'} onSec={() => setKategori('seyahat')} baslik="Seyahat (Ulaşım)" deger={o.ulasim} renk={r.ulasim}>
                  <Text type="secondary" style={{ fontSize: 12 }}>Uçak Bileti, Araç Kiralama, Yakıt · Şirket Öder</Text>
                </KategoriKarti>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <KategoriKarti secili={kategori === 'konaklama'} onSec={() => setKategori('konaklama')} baslik="Konaklama" deger={o.konaklama} renk={r.konaklama}>
                  <Text type="secondary" style={{ fontSize: 12 }}>{o.gece ? `${o.gece} Gece · Gece Başı ${para(o.gece_ort)}` : 'Konaklama Yok'} · Şirket Öder</Text>
                </KategoriKarti>
              </Col>
              <Col xs={12} sm={8} lg={4}><Card size="small"><Statistic title="Seyahat Sayısı" value={o.seyahat} /></Card></Col>
              <Col xs={12} sm={8} lg={4}><Card size="small"><Statistic title="Kişi" value={o.kisi} /></Card></Col>
              <Col xs={12} sm={8} lg={4}><Card size="small"><Statistic title="Seyahat Günü" value={o.gun} /></Card></Col>
              <Col xs={12} sm={12} lg={6}><Card size="small"><Statistic title="Günlük Gıda Ortalaması" value={o.gunluk_ort} formatter={(v) => para(v)} /></Card></Col>
              <Col xs={24} sm={12} lg={6}><Card size="small"><Statistic title="Onay Bekleyen Gıda" value={o.bekleyen} formatter={(v) => para(v)} /></Card></Col>
            </Row>
            {gidaGoster && o.isaretli_ogun > 0 && (
              <Alert type="warning" showIcon style={{ marginBottom: 12 }}
                message={`${o.isaretli_ogun} öğünde fiş kontrol işareti var (okunan ≠ girilen, fiş tarihi, şehir, aynı fiş ya da okunamadı). Seyahat ayrıntısında ⚠ simgesiyle görünür.`} />
            )}
            {gidaGoster && o.girilmemis_ogun > 0 && (
              <Alert type="info" showIcon style={{ marginBottom: 12 }}
                message={`Bu aralıkta harcırah hakkı olup çalışanın henüz tutar girmediği ${o.girilmemis_ogun} öğün var; toplamlara dahil değil.`} />
            )}
            {!K.deger(o) ? <Card size="small"><Empty description={`Bu aralıkta ${K.kisa.toLocaleLowerCase('tr')} tutarı yok`} /></Card> : (
              <Row gutter={[12, 12]}>
                {!kisi && (
                  <Col xs={24}>
                    <GrafikKarti baslik={baslik('En Çok Harcama Yapanlar')}
                      aciklama={`Seçili kategoride tutarı en yüksek ${ILK} kişi. Seyahat (ulaşım) ve konaklamayı şirket öder; seyahat eden kişiye yazılır. Pay = toplamın yüzdesi; gün başı = tutar / seyahat günü.`}
                      tablo={<Table size="small" pagination={false} rowKey="kullanici_no" dataSource={dolu(veri.kisiler).slice(0, ILK)}
                        scroll={{ x: 900 }} columns={[
                          { title: '#', key: 'sira', width: 44, render: (_, x, i) => i + 1 },
                          { title: 'Kişi', dataIndex: 'ad' },
                          { title: 'Seyahat', dataIndex: 'seyahat', align: 'right' }, { title: 'Gün', dataIndex: 'gun', align: 'right' },
                          { title: 'Pay', dataIndex: 'deger', key: 'pay', align: 'right', render: (v) => yuzde(v, K.deger(o)) },
                          { title: 'Gün Başı', key: 'gb', align: 'right', render: (_, x) => (x.gun ? para(x.deger / x.gun) : '—') },
                          ...katSutunlari]} />}>
                      <EnCokHarcayanlar kisiler={dolu(veri.kisiler)} K={K} r={r} toplam={K.deger(o)} ipucu={ipucu} />
                    </GrafikKarti>
                  </Col>
                )}
                <Col xs={24} xl={14}>
                  <GrafikKarti baslik={baslik('Seyahat Türüne Göre')}
                    aciklama="Seyahatin türü (keşif, kurulum, enerji…) bazında; türü girilmemiş eski seyahatler “Belirtilmemiş”."
                    tablo={<Table size="small" pagination={false} rowKey="tur" dataSource={veri.turler} scroll={{ x: 760 }} columns={[
                      { title: 'Seyahat Türü', dataIndex: 'ad' },
                      { title: 'Seyahat', dataIndex: 'seyahat', align: 'right' }, { title: 'Gün', dataIndex: 'gun', align: 'right' },
                      ...katSutunlari]} />}>
                    <YatayCubuk veri={dolu(veri.turler)} ad="ad" r={r} seriler={K.seriler} yigilmis
                      etiket={{ alan: 'deger', bicim: kisaPara }}
                      ipucu={(d) => [...ipucu(d), ['Seyahat / Gün', `${d.seyahat} / ${d.gun}`]]} />
                  </GrafikKarti>
                </Col>
                <Col xs={24} xl={10}>
                  <GrafikKarti baslik={baslik('Türlere Göre Pay')}
                    aciklama="Seçili kategorinin seyahat türlerine dağılımı. En büyük 5 tür ayrı, kalanlar “Diğer”de."
                    tablo={<Table size="small" pagination={false} rowKey="tur" dataSource={dolu(veri.turler)} columns={[
                      { title: 'Seyahat Türü', dataIndex: 'ad' },
                      { title: 'Pay', dataIndex: 'deger', align: 'right', render: (v) => yuzde(v, K.deger(o)) },
                      { title: 'Seyahat', dataIndex: 'seyahat', align: 'right' },
                      { title: K.ad, dataIndex: 'deger', key: 't', align: 'right', render: (v) => <b>{para(v)}</b> }]} />}>
                    <TurHalkasi turler={veri.turler.map((t) => ({ ...t, toplam: K.deger(t) }))} r={rTon} />
                  </GrafikKarti>
                </Col>
                {giderKartlari.includes('turler') && (
                  <Col {...giderGenislik}>
                    <GrafikKarti baslik="Gider Türlerine Göre"
                      aciklama="Şirketin ödediği seyahat giderleri belge türüne göre. Uçak bileti, araç kiralama ve yakıt seyahat (ulaşım); otel faturası konaklama."
                      tablo={<Table size="small" pagination={false} rowKey="tur" dataSource={veri.gider_turleri} columns={[
                        { title: 'Gider', dataIndex: 'ad' }, { title: 'Belge', dataIndex: 'adet', align: 'right' },
                        { title: 'Seyahat', dataIndex: 'seyahat', align: 'right' },
                        { title: 'Toplam', dataIndex: 'toplam', align: 'right', render: (v) => <b>{para(v)}</b> }]} />}>
                      <YatayCubuk ad="ad" r={r} seriler={kategori === 'seyahat' ? GIDER_KATEGORI.slice(0, 1) : GIDER_KATEGORI} yigilmis
                        etiket={{ alan: 'toplam', bicim: kisaPara }}
                        veri={veri.gider_turleri.filter((g) => kategori !== 'seyahat' || g.tur !== 'konaklama')
                          .map((g) => ({ ...g, ulasim: g.tur === 'konaklama' ? 0 : g.toplam, konaklama: g.tur === 'konaklama' ? g.toplam : 0 }))}
                        ipucu={(d) => [['Toplam', para(d.toplam), r[d.tur === 'konaklama' ? 'konaklama' : 'ulasim']], ['Belge', d.adet],
                          ['Seyahat', d.seyahat], ['Seyahat Başı', para(d.toplam / (d.seyahat || 1))]]} />
                    </GrafikKarti>
                  </Col>
                )}
                {giderKartlari.includes('ulasim') && (
                  <Col {...giderGenislik}>
                    <Card size="small" style={{ height: '100%' }}>
                      <KartBasligi baslik="Ulaşım Şekline Göre"
                        aciklama="Çalışanın seyahat ettiği şekil. Tutar yalnız seyahat (ulaşım) giderleridir (bilet, kiralama, yakıt); seyahat başı = tutar / seyahat sayısı." />
                      {veri.ulasim_sekilleri.map((u) => (
                        <div key={u.ulasim} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: '2px 12px', padding: '10px 0', borderBottom: `1px solid ${r.cizgi}` }}>
                          <Text strong>{u.ad}</Text>
                          <Text strong style={{ fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{para(u.toplam)}</Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>{u.seyahat} Seyahat</Text>
                          <Text type="secondary" style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>Seyahat Başı {para(u.toplam / (u.seyahat || 1))}</Text>
                        </div>
                      ))}
                    </Card>
                  </Col>
                )}
                {giderKartlari.includes('konaklama') && (
                  <Col {...giderGenislik}>
                    <GrafikKarti baslik="Gece Başı Konaklama"
                      aciklama="Şehir bazında otel faturası tutarı / gece sayısı; şehirler arası konaklama fiyat farkını gösterir."
                      tablo={<Table size="small" pagination={false} rowKey="il_no" dataSource={veri.konaklama_iller} columns={[
                        { title: 'Şehir', dataIndex: 'il' }, { title: 'Gece', dataIndex: 'gece', align: 'right' },
                        { title: 'Gece Başı', dataIndex: 'gece_ort', align: 'right', render: para },
                        { title: 'Toplam', dataIndex: 'toplam', align: 'right', render: (v) => <b>{para(v)}</b> }]} />}>
                      {veri.konaklama_iller.length ? (
                        <YatayCubuk ad="il" r={r} seriler={[{ key: 'konaklama', ad: 'Gece Başı' }]} etiket={{ bicim: kisaPara }}
                          veri={[...veri.konaklama_iller].sort((a, b) => b.gece_ort - a.gece_ort).map((k) => ({ ...k, konaklama: k.gece_ort }))}
                          ipucu={(d) => [['Gece Başı', para(d.gece_ort), r.konaklama], ['Gece', d.gece], ['Toplam', para(d.toplam)]]} />
                      ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Bu aralıkta konaklama yok" />}
                    </GrafikKarti>
                  </Col>
                )}
                <Col xs={24}>
                  <GrafikKarti baslik={baslik('Tür × Şehir')}
                    aciklama="Hangi tür seyahatin hangi şehirde ne kadar tuttuğu (seçili kategori). Kutuya gelince tutar ve gün sayısı görünür."
                    tablo={<Table size="small" pagination={false} rowKey={(x) => `${x.tur}|${x.il_no}`}
                      dataSource={dolu(veri.tur_il)} scroll={{ x: 760 }} columns={[
                        { title: 'Seyahat Türü', dataIndex: 'tur', render: (v) => veri.turler.find((t) => t.tur === v)?.ad || v },
                        { title: 'Şehir', dataIndex: 'il' }, { title: 'Gün', dataIndex: 'gun', align: 'right' }, ...katSutunlari]} />}>
                    <TurIlIsiHaritasi turIl={veri.tur_il.map((x) => ({ ...x, toplam: K.deger(x) }))}
                      turler={veri.turler.map((t) => ({ ...t, toplam: K.deger(t) }))} r={rTon} />
                  </GrafikKarti>
                </Col>
                <Col xs={24} xl={12}>
                  <GrafikKarti baslik={baslik('Şehir Bazında')}
                    tablo={<Table size="small" pagination={false} rowKey="il_no" dataSource={veri.iller} scroll={{ x: 640 }} columns={[
                      { title: 'Şehir', dataIndex: 'il' }, { title: 'Gün', dataIndex: 'gun', align: 'right' }, ...katSutunlari]} />}>
                    <YatayCubuk veri={dolu(veri.iller)} ad="il" r={r} seriler={K.seriler} yigilmis
                      etiket={{ alan: 'deger', bicim: kisaPara }} ipucu={ipucu} />
                  </GrafikKarti>
                </Col>
                {gidaGoster && (
                  <Col xs={24} xl={12}>
                    <GrafikKarti baslik="Şehre Göre Ortalama Öğün Tutarı (Gıda)"
                      aciklama="Tutar girilmiş öğünlerin ortalaması — şehirler arası yemek fiyatı farkını gösterir."
                      tablo={<Table size="small" pagination={false} rowKey="il_no" dataSource={veri.iller.filter((i) => i.toplam > 0)} columns={[
                        { title: 'Şehir', dataIndex: 'il' },
                        { title: 'Öğle Ort.', dataIndex: 'ogle_ort', align: 'right', render: (v) => (v == null ? '—' : para(v)) },
                        { title: 'Öğle Adet', dataIndex: 'ogle_adet', align: 'right' },
                        { title: 'Akşam Ort.', dataIndex: 'aksam_ort', align: 'right', render: (v) => (v == null ? '—' : para(v)) },
                        { title: 'Akşam Adet', dataIndex: 'aksam_adet', align: 'right' }]} />}>
                      <YatayCubuk veri={veri.iller.filter((i) => i.toplam > 0).map((i) => ({ ...i, ogle: i.ogle_ort || 0, aksam: i.aksam_ort || 0 }))}
                        ad="il" r={r} seriler={BARLAR} etiket={{ bicim: kisaPara }}
                        ipucu={(d) => [['Öğle Ort.', d.ogle_ort == null ? '—' : `${para(d.ogle_ort)} (${d.ogle_adet})`, r.ogle],
                          ['Akşam Ort.', d.aksam_ort == null ? '—' : `${para(d.aksam_ort)} (${d.aksam_adet})`, r.aksam]]} />
                    </GrafikKarti>
                  </Col>
                )}
                <Col xs={24} xl={12}>
                  <GrafikKarti baslik={baslik('Kişi Bazında')}
                    tablo={<Table size="small" pagination={false} rowKey="kullanici_no" dataSource={veri.kisiler} scroll={{ x: 640 }} columns={[
                      { title: 'Kişi', dataIndex: 'ad' }, { title: 'Gün', dataIndex: 'gun', align: 'right' }, ...katSutunlari]} />}>
                    <YatayCubuk veri={dolu(veri.kisiler)} ad="ad" r={r} seriler={K.seriler} yigilmis
                      etiket={{ alan: 'deger', bicim: kisaPara }} ipucu={ipucu} />
                  </GrafikKarti>
                </Col>
                <Col xs={24} xl={gidaGoster ? 12 : 24}>
                  <GrafikKarti baslik={baslik(veri.periyot === 'ay' ? 'Aylık' : 'Haftalık')}
                    tablo={<Table size="small" pagination={false} rowKey="donem" dataSource={zaman} scroll={{ x: 640 }} columns={[
                      { title: veri.periyot === 'ay' ? 'Ay' : 'Hafta (Pzt)', dataIndex: 'etiket' }, ...katSutunlari]} />}>
                    {K.seriler.length > 1 && <Aciklama r={r} seriler={K.seriler} />}
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={zaman} margin={{ top: 20, right: 8, bottom: 4, left: 4 }} barCategoryGap="30%">
                        <CartesianGrid vertical={false} stroke={r.cizgi} />
                        <XAxis dataKey="etiket" tick={{ fill: r.yazi2, fontSize: 11 }} axisLine={{ stroke: r.cizgi }} tickLine={false} />
                        <YAxis tickFormatter={eksen} tick={{ fill: r.yazi2, fontSize: 11 }} axisLine={false} tickLine={false} width={56} />
                        <RTooltip cursor={{ fill: r.cizgi, opacity: 0.4 }}
                          content={(p) => <IpucuKutusu {...p} r={r} baslik={(d) => (veri.periyot === 'ay' ? d.etiket : `${d.etiket} Haftası`)} satirlar={ipucu} />} />
                        {K.seriler.map((sr, i) => (
                          <Bar key={sr.key} dataKey={sr.key} stackId="a" fill={r[sr.key]} stroke={r.yuzey} strokeWidth={2} maxBarSize={36}
                            radius={i === K.seriler.length - 1 ? [4, 4, 0, 0] : 0} isAnimationActive={false}>
                            {i === K.seriler.length - 1 && (
                              <LabelList dataKey="deger" position="top" formatter={(v) => (v ? kisaPara(v) : '')} style={{ fill: r.yazi2, fontSize: 11 }} />
                            )}
                          </Bar>
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </GrafikKarti>
                </Col>
                <Col xs={24}>
                  <Card size="small">
                    <KartBasligi baslik={baslik('Onay Durumu')} sag={<Text type="secondary" style={{ fontSize: 12 }}>Durum Süzgecinden Bağımsız</Text>} />
                    <DurumCubugu durumlar={veri.durumlar.map((d) => ({ ...d, toplam: K.deger(d) }))} r={r} />
                  </Card>
                </Col>
              </Row>
            )}
          </>
        )}
      </Spin>
    </div>
  );
}
